import { Dropdown } from "primereact/dropdown";
import { useEffect, useRef, useState } from "react";
import styles from "./KrogerReport.module.scss";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import * as constants from "../../constants/constants";
import {
  regionSummaryColumns,
  createRegionSummaryHeaderGroup
} from "../../constants/table-columns/krogerReportTableColumns";
import { getRegionSummary } from "../../services/KrogerReportService";
import Spinner from "../../components/spinner/spinner";
import { Button } from "primereact/button";
import { formatInTimeZone } from "date-fns-tz";
import { Toast } from "primereact/toast";

const RegionSummary = ({ dateRange }) => {
  const toast = useRef();
  const [selectedRegion, setSelectedRegion] = useState("All");
  const [excludedUnits, setExcludedUnits] = useState([]);
  const [apiData, setApiData] = useState(null);
  const [showSpinner, setShowSpinner] = useState(false);
  const { startDate, endDate } = dateRange;

  const krogerRegions = ["All", "TFTN", "TFNW", "TFSW", "TFTX"];
  const BUSINESS_UNITS = [
    "Fresh Kitchen Deli",
    "Fresh Kitchen Produce",
    "Retail Produce"
  ];
  const convertToPacific = (date) => {
    if (!date) return null;
    return formatInTimeZone(
      new Date(date),
      "America/Los_Angeles",
      "yyyy-MM-dd'T'HH:mm:ss.SSS"
    );
  };
  useEffect(() => {
    if (!startDate || !endDate || !selectedRegion) {
      setApiData(null);
      return;
    }
    const fetchRegionSummary = async () => {
      setShowSpinner(true);
      const payload = {
        startDate: convertToPacific(startDate),
        endDate: convertToPacific(endDate),
        regions:
          selectedRegion === "All"
            ? ["TFTN", "TFNW", "TFSW", "TFTX"]
            : [selectedRegion]
      };
      try {
        setShowSpinner(true);
        const result = await getRegionSummary(payload);
        setApiData(result);
        setShowSpinner(false);
      } catch (error) {
        setShowSpinner(false);
        toast?.current?.show({
          severity: "error",
          summary: "Error",
          detail: error?.message,
          life: 4000
        });
      }
    };
    fetchRegionSummary();
  }, [startDate, endDate, selectedRegion]);

  const toggleUnit = (unit) =>
    setExcludedUnits((prev) =>
      prev.includes(unit) ? prev.filter((u) => u !== unit) : [...prev, unit]
    );

  const formatCurrency = (value) =>
    Number(value ?? 0).toLocaleString("en-US", {
      style: "currency",
      currency: "USD"
    });

  const getRegionRows = (region) => {
    return (region?.weeks ?? []).flatMap((week) => {
      const stores = (week?.stores ?? []).map((store, index) => ({
        ...store,
        rowId: `${region.region}-${week.weekNo}-${store.storeName}-${index}`,
        rowType: "store",
        region: region.region,
        weekNo: week.weekNo,
        weekLabel: week.weekLabel
      }));
      const totalsRow = {
        rowId: `${region.region}-${week.weekNo}-totals`,
        rowType: "weekTotals",
        storeName: week.weekLabel,
        deli: week?.weekTotals?.deli ?? {},
        produce: week?.weekTotals?.produce ?? {},
        krogerRetail: week?.weekTotals?.krogerRetail ?? {},
        creditGrandTotal: week?.weekTotals?.creditGrandTotal ?? 0
      };
      return [...stores, totalsRow];
    });
  };

  const visibleColumns = regionSummaryColumns.filter(({ group }) => {
    if (excludedUnits.includes("Fresh Kitchen Deli") && group === "deli") {
      return false;
    }
    if (
      excludedUnits.includes("Fresh Kitchen Produce") &&
      group === "produce"
    ) {
      return false;
    }
    if (excludedUnits.includes("Retail Produce") && group === "krogerRetail") {
      return false;
    }
    return true;
  });

  const headerGroup = createRegionSummaryHeaderGroup(visibleColumns);

  const renderCell = (row, column) => {
    const { field, group } = column;
    if (field === "storeName") {
      return row.storeName ?? "";
    }
    if (field === "creditGrandTotal") {
      return formatCurrency(row.creditGrandTotal);
    }
    if (group) {
      return formatCurrency(row[group]?.[field]);
    }
    return formatCurrency(row[field]);
  };
  const regions = apiData?.regions ?? [];
  const regionsToDisplay =
    selectedRegion === "All"
      ? regions
      : regions.filter((region) => region?.region === selectedRegion);

  const hasData = regionsToDisplay.length > 0;

  return (
    <div>
      <Toast ref={toast} position="center" />
      {showSpinner && <Spinner />}
      <div className="grid">
        <div className="col-12 lg:col-4">
          <span>Select Region: </span>
          <Dropdown
            options={krogerRegions}
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.value)}
            placeholder="Select Region"
            className={styles?.dropdown}
          />
        </div>
      </div>

      <div className={styles.filterBar}>
        {BUSINESS_UNITS?.map((unit) => {
          const active = !excludedUnits.includes(unit);
          return (
            <button
              key={unit}
              type="button"
              aria-pressed={active}
              className={`${styles.filterChip} ${active ? styles.filterChipActive : ""}`}
              onClick={() => toggleUnit(unit)}
            >
              <i className={`pi ${active ? "pi-check" : "pi-plus"}`} />
              {unit}
            </button>
          );
        })}
        <div className="ml-auto">
          <Button className={styles.downloadButton}>
            <i className="pi pi-download" />
            <span>Download</span>
          </Button>
        </div>
      </div>
      {(hasData
        ? regionsToDisplay
        : [{ region: selectedRegion || " ", weeks: [] }]
      ).map((region) => {
        const storeRows = getRegionRows(region);

        return (
          <div key={region.region} className={styles.card}>
            <h2 className={styles.regionTitle}>
              Region Summary {selectedRegion ? `– ${region.region}` : ""}
            </h2>
            <DataTable
              className={styles.regionDataGrid}
              value={storeRows}
              dataKey="rowId"
              scrollable
              scrollHeight="30vw"
              tableStyle={{
                minWidth: "50rem",
                width: "max-content"
              }}
              headerColumnGroup={headerGroup}
              paginator={constants?.paginator}
              rows={100}
              rowClassName={(row) =>
                row?.rowType === "weekTotals" ? "weekTotalsRow" : ""
              }
              rowsPerPageOptions={constants?.rowsPerPageOptions}
              paginatorTemplate={constants?.paginatorTemplate}
              currentPageReportTemplate={constants?.currentPageReportTemplate}
              emptyMessage={constants?.emptyMessage}
            >
              {visibleColumns?.map(({ field, header, minWidth, group }) => (
                <Column
                  key={`${group ?? "main"}-${field}`}
                  field={field}
                  header={header}
                  style={{
                    minWidth,
                    width: minWidth
                  }}
                  body={(row) => renderCell(row, { field, group })}
                  alignHeader={field === "storeName" ? "left" : "center"}
                  bodyClassName={(row) =>
                    `${row?.rowType === "weekTotals" ? "font-bold " : ""}${
                      group === "krogerRetail" ? "retailDataCell" : ""
                    }`
                  }
                />
              ))}
            </DataTable>
          </div>
        );
      })}
    </div>
  );
};

export default RegionSummary;
